import "./navigation.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { Backdrop } from "@ts/utils";

type NavigationHook = () => void | Promise<void>;
type NavigationOptions = {
  onClick?: NavigationHook;
  onOpen?: NavigationHook;
  onClose?: NavigationHook;
  menuContainerSelector?: string;
  offsetElements?: string[];
};

export class Navigation {
    private readonly id: string;
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    private static registry = new ComponentRegistry<Navigation>('navigation');

    private _config = {
        menuOpenClass: 'nav-open',
        resizingClass: 'resizing',
        mdBreakpoint: window.matchMedia('(min-width: 768px)'),
        lgBreakpoint: window.matchMedia('(min-width: 992px)'),
        resizeDebounceTime: 150
    };

private body = document.body;
    private originalIconHtml = '';
    private menuContainer: HTMLElement | null = null;
    private resizeTimeout: number | undefined;
    private escapeKeyBound = false;
    private mobileBreakpoint = window.matchMedia('(max-width: 991px)');
    private dropdownOutsideClickBound = false;
    

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: NavigationOptions
    ) {
        this.id = Navigation.registry.register(trigger, this);
        this.trigger = trigger;
        this.originalIconHtml = trigger.innerHTML;
        
        this.menuContainer = document.querySelector(this.options.menuContainerSelector ?? '.main-nav-container');

        this.setClickEvent();
        this.bindEscapeKey();
        this.bindMobileSubnav();
        this.bindResizeListener();
        window.addEventListener('pagehide', this.handlePageTransition);
        window.addEventListener('pageshow', this.handlePageTransition);
        this.bindDropdownLogging();
    }

    private setNavigationMenuAria = (isOpen: boolean): void => {
        this.trigger?.setAttribute('aria-expanded', String(isOpen));
        this.menuContainer?.setAttribute('aria-hidden', String(!isOpen));
        this.toggleIcon(isOpen);
    }

    private toggleIcon = (isOpen: boolean): void => {
        if (!this.trigger) return;

        if (isOpen) {
            this.trigger.innerHTML = '<i class="fa-solid fa-xmark"></i>';
        } else {
            this.trigger.innerHTML = this.originalIconHtml;
        }
    }

    private bindEscapeKey(): void {
        if (this.escapeKeyBound) return;

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (this.body.classList.contains(this._config.menuOpenClass)) {
                    this.toggleMenu(false);
                }
            }
        });

        this.escapeKeyBound = true;
    }
    private bindMobileSubnav(): void {
        if (!this.menuContainer) return;

        this.menuContainer.addEventListener('click', (event: Event) => {
            if (!this.mobileBreakpoint.matches) return;

            const target = event.target as HTMLElement | null;
            if (!target) return;

            const navLink = target.closest('.dropdown-toggle') as HTMLElement | null;
            if (!navLink) return;

            const navItem = navLink.closest('.nav-item.dropdown') as HTMLElement | null;
            const navbarNav = navLink.closest('.navbar-nav') as HTMLElement | null;
            if (!navItem || !navbarNav) return;

            const dropdownMenu = navItem.querySelector('.dropdown-menu') as HTMLElement | null;
            if (!dropdownMenu) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            const isSubnavOpen = navbarNav.classList.contains('is-subnav-open');
            const isActiveParent = navItem.classList.contains('nav-item--active-parent');

            if (isSubnavOpen && isActiveParent) {
                this.resetMobileSubnav(navbarNav);
                return;
            }

            this.openMobileSubnav(navbarNav, navItem);
        }, true);
    }

    private openMobileSubnav(
        navbarNav: HTMLElement,
        parentItem: HTMLElement
    ): void {
        navbarNav
            .querySelectorAll('.nav-item--active-parent')
            .forEach(li => li.classList.remove('nav-item--active-parent'));

        navbarNav
            .querySelectorAll('.dropdown-menu.show')
            .forEach(menu => menu.classList.remove('show'));

        navbarNav
            .querySelectorAll('.nav-item.show, .dropdown.show')
            .forEach(item => item.classList.remove('show'));

        parentItem.classList.add('nav-item--active-parent');
        navbarNav.classList.add('is-subnav-open');
    }

    private resetMobileSubnav(navbarNav?: HTMLElement | null): void {
        const navRoot = navbarNav ?? (this.menuContainer?.querySelector('.navbar-nav') as HTMLElement | null);
        if (!navRoot) return;

        navRoot.classList.remove('is-subnav-open');

        navRoot
            .querySelectorAll('.nav-item--active-parent')
            .forEach(li => li.classList.remove('nav-item--active-parent'));

        navRoot
            .querySelectorAll('.dropdown-menu.show')
            .forEach(menu => menu.classList.remove('show'));

        navRoot
            .querySelectorAll('.nav-item.show, .dropdown.show')
            .forEach(item => item.classList.remove('show'));
    }

    private bindResizeListener(): void {
        window.addEventListener('resize', this.handleResize);
    }

    private calculateMenuOffset(): number {
        let offsetHeight = 0;

        // Include body padding-top if present
        const bodyStyles = window.getComputedStyle(this.body);
        const bodyPaddingTop = parseFloat(bodyStyles.paddingTop) || 0;
        offsetHeight += bodyPaddingTop;

        const pageHeaderContainer = document.querySelector('.page-header-container');
        if (pageHeaderContainer) {
            offsetHeight += pageHeaderContainer.clientHeight;
        }

        if (this.options.offsetElements) {
            this.options.offsetElements.forEach(selector => {
                const element = document.querySelector(selector);
                if (element) {
                    offsetHeight += (element as HTMLElement).clientHeight;
                }
            });
        }

        return offsetHeight;
    }

    private applyMenuOffset(apply: boolean): void {
        if (!this.menuContainer) return;

        if (apply && this.mobileBreakpoint.matches) {
            const offset = this.calculateMenuOffset();
            this.menuContainer.style.marginTop = `${offset}px`;
        } else {
            this.menuContainer.style.marginTop = '';
        }
    }

    private toggleMenu = (openState?: boolean): void => {
        const isOpen = openState ?? !this.body.classList.contains(this._config.menuOpenClass);

        this.body.classList.toggle(this._config.menuOpenClass, isOpen);
        
        if (this.menuContainer) {
            const containerClass = this.menuContainer.classList[0] || 'menu';
            const menuOpenClass = `${containerClass}-is-open`;
            this.body.classList.toggle(menuOpenClass, isOpen);
        }
        
        this.setNavigationMenuAria(isOpen);

        this.applyMenuOffset(isOpen);

        if (isOpen && typeof this.options.onOpen === 'function') {
            this.options.onOpen();
        } else if (!isOpen && typeof this.options.onClose === 'function') {
            this.options.onClose();
        }

        if (isOpen) {
            const backdrop = new Backdrop(() => this.toggleMenu(false));
            backdrop.show();
        } else {
            const navbarNav = this.menuContainer?.querySelector('.navbar-nav') as HTMLElement | null;
            this.resetMobileSubnav(navbarNav);

            const existingBackdrop = document.querySelector('.nav-backdrop') as HTMLDivElement;
            if (existingBackdrop) {
                existingBackdrop.remove();
            }
        }
    }

    private setClickEvent(): void {
        this.trigger.addEventListener('click', (e) => {
            e.preventDefault();
            if (typeof this.options.onClick === 'function') {
                this.options.onClick();
            }
            this.toggleMenu();
        });
    }

    public handleResize = (): void => {
        const mainNavContainer = this.menuContainer ?? document.querySelector('.main-nav-container') as HTMLElement | null;
        if (!mainNavContainer) return;

        mainNavContainer.classList.add(this._config.resizingClass);

        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = window.setTimeout(() => {
            mainNavContainer.classList.remove(this._config.resizingClass);

            if (this._config.mdBreakpoint.matches) {
                this.body.classList.remove(this._config.menuOpenClass);

                // Ensure the per-container open class (e.g. main-nav-container-is-open) is also removed
                const containerClass = mainNavContainer.classList[0] || 'menu';
                const menuOpenClass = `${containerClass}-is-open`;
                this.body.classList.remove(menuOpenClass);

                this.setNavigationMenuAria(false);
                this.applyMenuOffset(false);

                const navbarNav = mainNavContainer.querySelector('.navbar-nav') as HTMLElement | null;
                this.resetMobileSubnav(navbarNav);

                const existingBackdrop = document.querySelector('.nav-backdrop') as HTMLDivElement;
                if (existingBackdrop) {
                    existingBackdrop.remove();
                }

                // Close any open dropdowns by triggering click on expanded toggles
                document.querySelectorAll<HTMLElement>('.dropdown-toggle[aria-expanded="true"]').forEach(toggle => {
                    toggle.click();
                });
            } else {
                this.applyMenuOffset(true);
            }
        }, this._config.resizeDebounceTime);
    };

    // Reset before caching and after history restoration, including browser Back.
    private handlePageTransition = (): void => {
        this.close();
    };

    public destroy(): void {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('pagehide', this.handlePageTransition);
        window.removeEventListener('pageshow', this.handlePageTransition);
        Navigation.registry.unregister(this.id);
    }

    private onDropdownShown(
        dropdown: HTMLElement,
    ): void {
        document.body.classList.add('nav-dropdown-open');
        dropdown.classList.add('active');
    }

    private onDropdownHidden(dropdown?: HTMLElement): void {
        if (dropdown) {
            dropdown.classList.remove('active');
            const anyActive = document.querySelector(
                '.nav-item.dropdown.active, .nav-item.shop-changer.active'
            );
            if (!anyActive) {
                document.body.classList.remove('nav-dropdown-open');
            }
        } else {
            document.body.classList.remove('nav-dropdown-open');

            document
                .querySelectorAll<HTMLElement>('.nav-item.dropdown.active, .nav-item.shop-changer.active')
                .forEach(el => el.classList.remove('active'));
        }
    }

    private bindDropdownLogging(): void {
        const dropdownHosts: HTMLElement[] = [];

        if (this.menuContainer) {
            dropdownHosts.push(
                ...Array.from(this.menuContainer.querySelectorAll<HTMLElement>('.nav-item.dropdown'))
            );
        }

        dropdownHosts.push(
            ...Array.from(document.querySelectorAll<HTMLElement>('.shop-changer.nav-item.dropdown'))
        );

        const uniqueDropdownHosts = Array.from(new Set(dropdownHosts));

        uniqueDropdownHosts.forEach(dropdown => {
            const toggle = dropdown.querySelector('.dropdown-toggle') as HTMLElement | null;
            const menu = dropdown.querySelector('.dropdown-menu') as HTMLElement | null;

            if (!toggle) return;

            toggle.addEventListener('click', (event: MouseEvent) => {
                const isDesktop = this._config.lgBreakpoint.matches;

                if (isDesktop) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    return;
                }

                const isOpen = menu ? menu.classList.contains('show') : dropdown.classList.contains('show');
                const willOpen = !isOpen;

                if (willOpen) {
                    this.onDropdownShown(dropdown);
                } else {
                    this.onDropdownHidden(dropdown);
                }
            });

            dropdown.addEventListener('mouseenter', () => {
                if (!this._config.lgBreakpoint.matches) return;
                this.onDropdownShown(dropdown);
            });

            dropdown.addEventListener('mouseleave', (event: MouseEvent) => {
                if (!this._config.lgBreakpoint.matches) return;
                const related = event.relatedTarget as Node | null;
                if (related && dropdown.contains(related)) return;

                this.onDropdownHidden(dropdown);
            });
        });

        if (!this.dropdownOutsideClickBound) {
            document.addEventListener('click', (event: MouseEvent) => {
                if (!document.body.classList.contains('nav-dropdown-open')) return;

                const target = event.target as HTMLElement | null;
                if (!target) return;

                const activeDropdowns = document.querySelectorAll<HTMLElement>(
                    '.nav-item.dropdown.active, .nav-item.shop-changer.active'
                );
                for (const dropdown of Array.from(activeDropdowns)) {
                    if (dropdown.contains(target)) {
                        return;
                    }
                }
               this.onDropdownHidden();
            });
            this.dropdownOutsideClickBound = true;
        }
    }

    public close(): void {
        if (this.body.classList.contains(this._config.menuOpenClass)) {
            this.toggleMenu(false);
        }
        this.resetMobileSubnav();
        this.menuContainer
            ?.querySelectorAll('.dropdown-menu.show, .dropdown-toggle.show, .nav-item.dropdown.show')
            .forEach(element => element.classList.remove('show'));
        this.menuContainer
            ?.querySelectorAll('.dropdown-toggle[aria-expanded="true"]')
            .forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
        this.onDropdownHidden();
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: NavigationOptions = {}
    ): Navigation[] | null {

        const triggers = ComponentRegistry.processTargets<Navigation, HTMLElement>(
            Navigation.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;

        return triggers.map(trigger => new Navigation(trigger, options));
    }

    public static getNavigation(id: string): Navigation | null {
        return Navigation.registry.get(id) ?? null;
    }

    public static close(): void {
        for (const id of Navigation.registry.getIds()) {
            const instance = Navigation.registry.get(id);
            if (instance) {
                instance.close();
            }
        }
    }
}